import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Search,
  Clock,
  TrendingUp,
  X,
  Pill,
} from "lucide-react";
import api, { imgUrl, eur } from "../../lib/api";
import { scoreProduct } from "../../lib/catalog";

let _cache = null; // module-level product cache for instant search

/*
 * =========================================================
 * RECENT SEARCHES
 * =========================================================
 */

function getRecent() {
  try {
    return JSON.parse(
      localStorage.getItem("recentSearches") || "[]"
    );
  } catch {
    return [];
  }
}

function pushRecent(q) {
  const term = q.trim();

  if (!term) return;

  const next = [
    term,
    ...getRecent().filter(
      (t) =>
        t.toLowerCase() !== term.toLowerCase()
    ),
  ].slice(0, 6);

  localStorage.setItem(
    "recentSearches",
    JSON.stringify(next)
  );
}

/*
 * =========================================================
 * SEARCH BOX
 * =========================================================
 */

export default function SearchBox({
  autoFocus = false,
  onNavigate,
}) {
  const nav = useNavigate();

  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [products, setProducts] = useState([]);
  const [recent, setRecent] = useState(getRecent());
  const [popular, setPopular] = useState([]);

  const boxRef = useRef(null);

  /*
   * =======================================================
   * LOAD PRODUCTS
   * =======================================================
   */

  useEffect(() => {
    const load = async () => {
      if (_cache) {
        setProducts(_cache);

        /*
         * Υπολογισμός δημοφιλών brands από το cache.
         */

        const counts = {};

        _cache.forEach((p) => {
          if (p.brand) {
            counts[p.brand] =
              (counts[p.brand] || 0) + 1;
          }
        });

        setPopular(
          Object.entries(counts)
            .sort((a, b) => b[1] - a[1])
            .slice(0, 6)
            .map(([b]) => b)
        );

        return;
      }

      try {
        const { data } = await api.get("/products");

        _cache = data;

        setProducts(data);

        /*
         * Δημοφιλείς μάρκες
         */

        const counts = {};

        data.forEach((p) => {
          if (p.brand) {
            counts[p.brand] =
              (counts[p.brand] || 0) + 1;
          }
        });

        setPopular(
          Object.entries(counts)
            .sort((a, b) => b[1] - a[1])
            .slice(0, 6)
            .map(([b]) => b)
        );
      } catch {
        setProducts([]);
        setPopular([]);
      }
    };

    load();
  }, []);

  /*
   * =======================================================
   * CLOSE DROPDOWN WHEN CLICKING OUTSIDE
   * =======================================================
   */

  useEffect(() => {
    const onClick = (e) => {
      if (
        boxRef.current &&
        !boxRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    };

    document.addEventListener(
      "mousedown",
      onClick
    );

    return () => {
      document.removeEventListener(
        "mousedown",
        onClick
      );
    };
  }, []);

  /*
   * =======================================================
   * SEARCH RESULTS
   * =======================================================
   */

  const results = q.trim()
    ? products
        .map((p) => ({
          p,
          s: scoreProduct(p, q),
        }))
        .filter((x) => x.s >= 0)
        .sort((a, b) => b.s - a.s)
        .slice(0, 7)
        .map((x) => x.p)
    : [];

  /*
   * =======================================================
   * FULL SEARCH
   * =======================================================
   */

  const goSearch = (term) => {
    const t = (term ?? q).trim();

    if (!t) return;

    pushRecent(t);

    setRecent(getRecent());

    setOpen(false);
    setQ("");

    onNavigate?.();

    nav(
      `/shop?search=${encodeURIComponent(t)}`
    );
  };

  /*
   * =======================================================
   * PRODUCT NAVIGATION
   * =======================================================
   *
   * ΠΡΟΤΕΡΑΙΟΤΗΤΑ:
   *
   * 1. product.slug
   * 2. product.id
   *
   * Έτσι χρησιμοποιούμε SEO-friendly URLs.
   *
   * Παράδειγμα:
   *
   * /product/mollers-omega-3-gummies-cola
   *
   * αντί:
   *
   * /product/65a8...
   */

  const goProduct = (p) => {
    pushRecent(p.name);

    setRecent(getRecent());

    setOpen(false);
    setQ("");

    onNavigate?.();

    const productIdentifier =
      p.slug || p.id;

    if (!productIdentifier) return;

    nav(
      `/product/${encodeURIComponent(
        productIdentifier
      )}`
    );
  };

  /*
   * =======================================================
   * CLEAR RECENT SEARCHES
   * =======================================================
   */

  const clearRecent = () => {
    localStorage.removeItem(
      "recentSearches"
    );

    setRecent([]);
  };

  /*
   * =======================================================
   * RENDER
   * =======================================================
   */

  return (
    <div
      ref={boxRef}
      className="relative w-full"
      data-testid="search-form"
    >
      {/* ===================================================
          SEARCH FORM
      ==================================================== */}

      <form
        onSubmit={(e) => {
          e.preventDefault();
          goSearch();
        }}
        role="search"
      >
        <div className="relative">
          <Search
            className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#536D61]"
            aria-hidden="true"
          />

          <input
            data-testid="search-input"
            autoFocus={autoFocus}
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setOpen(true);
            }}
            onFocus={() => setOpen(true)}
            placeholder="Αναζήτηση προϊόντων, μάρκας, κωδικού..."
            aria-label="Αναζήτηση προϊόντων, μάρκας ή κωδικού"
            autoComplete="off"
            className="w-full pl-10 pr-9 py-2.5 rounded-full border border-[#E5E9E4] bg-white text-sm focus:border-[#1E3A2F] focus:ring-1 focus:ring-[#1E3A2F] outline-none"
          />

          {q && (
            <button
              type="button"
              onClick={() => {
                setQ("");
                setOpen(true);
              }}
              aria-label="Καθαρισμός αναζήτησης"
              className="absolute right-3 top-1/2 -translate-y-1/2 text-[#536D61] hover:text-[#1E3A2F]"
            >
              <X
                className="h-4 w-4"
                aria-hidden="true"
              />
            </button>
          )}
        </div>
      </form>

      {/* ===================================================
          SEARCH DROPDOWN
      ==================================================== */}

      {open && (
        <div
          className="absolute z-50 mt-2 w-full bg-white border border-[#E5E9E4] rounded-xl shadow-xl overflow-hidden"
          data-testid="search-dropdown"
        >
          {/* =================================================
              SEARCH RESULTS
          ================================================== */}

          {q.trim() ? (
            results.length > 0 ? (
              <ul className="max-h-[60vh] overflow-auto">
                {results.map((p) => (
                  <li key={p.id}>
                    <button
                      type="button"
                      onClick={() =>
                        goProduct(p)
                      }
                      data-testid={`search-result-${p.id}`}
                      aria-label={`Άνοιγμα προϊόντος ${p.name}`}
                      className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-[#E8F0E1] text-left"
                    >
                      {/* PRODUCT IMAGE */}

                      <div className="h-11 w-11 rounded-lg overflow-hidden bg-[#E8F0E1] shrink-0 flex items-center justify-center">
                        {p.images?.[0] ? (
                          <img
                            src={imgUrl(
                              p.images[0]
                            )}
                            alt={`${p.name} | Φαρμακείο Κατιρτζόγλου`}
                            className="h-full w-full object-cover"
                            loading="lazy"
                          />
                        ) : (
                          <Pill
                            className="h-5 w-5 text-[#1E3A2F]/40"
                            aria-hidden="true"
                          />
                        )}
                      </div>

                      {/* PRODUCT INFORMATION */}

                      <div className="min-w-0 flex-1">
                        {p.brand && (
                          <p className="text-[11px] uppercase tracking-wide text-[#536D61]">
                            {p.brand}
                          </p>
                        )}

                        <p className="text-sm text-[#1E3A2F] truncate">
                          {p.name}
                        </p>
                      </div>

                      {/* PRICE */}

                      <span className="text-sm font-semibold text-[#1E3A2F] whitespace-nowrap">
                        {eur(p.price)}
                      </span>
                    </button>
                  </li>
                ))}

                {/* =========================================
                    ALL SEARCH RESULTS
                ========================================== */}

                <li className="border-t border-[#E5E9E4]">
                  <button
                    type="button"
                    onClick={() =>
                      goSearch()
                    }
                    className="w-full text-center py-2.5 text-sm font-medium text-[#1E3A2F] hover:bg-[#E8F0E1]"
                  >
                    Όλα τα αποτελέσματα για
                    {" "}
                    «{q}»
                  </button>
                </li>
              </ul>
            ) : (
              /* ===========================================
                 NO RESULTS
              ============================================ */

              <div className="px-4 py-6 text-center text-sm text-[#536D61]">
                Δεν βρέθηκαν προϊόντα για
                {" "}
                «{q}».
              </div>
            )
          ) : (
            /* =================================================
               EMPTY SEARCH STATE
            ================================================== */

            <div className="p-3 space-y-3">

              {/* =============================================
                  RECENT SEARCHES
              ============================================== */}

              {recent.length > 0 && (
                <div>
                  <div className="flex items-center justify-between px-1 mb-1">
                    <span className="text-xs font-semibold text-[#536D61] flex items-center gap-1.5">
                      <Clock
                        className="h-3.5 w-3.5"
                        aria-hidden="true"
                      />

                      Πρόσφατες αναζητήσεις
                    </span>

                    <button
                      type="button"
                      onClick={clearRecent}
                      className="text-xs text-[#536D61] hover:text-[#C25953]"
                    >
                      Καθαρισμός
                    </button>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    {recent.map((t) => (
                      <button
                        key={t}
                        type="button"
                        onClick={() =>
                          goSearch(t)
                        }
                        className="text-sm px-3 py-1.5 rounded-full bg-[#F9F8F6] border border-[#E5E9E4] text-[#1E3A2F] hover:bg-[#E8F0E1]"
                      >
                        {t}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* =============================================
                  POPULAR BRANDS
              ============================================== */}

              {popular.length > 0 && (
                <div>
                  <span className="text-xs font-semibold text-[#536D61] flex items-center gap-1.5 px-1 mb-1">
                    <TrendingUp
                      className="h-3.5 w-3.5"
                      aria-hidden="true"
                    />

                    Δημοφιλείς μάρκες
                  </span>

                  <div className="flex flex-wrap gap-2">
                    {popular.map((t) => (
                      <button
                        key={t}
                        type="button"
                        onClick={() =>
                          goSearch(t)
                        }
                        className="text-sm px-3 py-1.5 rounded-full bg-[#F9F8F6] border border-[#E5E9E4] text-[#1E3A2F] hover:bg-[#E8F0E1]"
                      >
                        {t}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* =============================================
                  EMPTY STATE
              ============================================== */}

              {recent.length === 0 &&
                popular.length === 0 && (
                  <div className="px-2 py-4 text-center text-sm text-[#536D61]">
                    Ξεκινήστε να πληκτρολογείτε
                    για αναζήτηση.
                  </div>
                )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/*
 * =========================================================
 * EXPORT CACHE
 * =========================================================
 */

export { _cache };